import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Edit2, MapPinned, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  code: '',
  name: '',
  commercialHierarchyParent: '',
  commercialHierarchyChild: '',
  origin: '1',
  minimumOrderValue: '',
  hasSuggestedLoad: false,
};

const TerritoriesPage = () => {
  const { isAdmin } = useAuth();
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadTerritories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadTerritories = async () => {
    try {
      setLoading(true);
      const response = await api.getTerritories({ page: 1, limit: 100, search });
      setTerritories(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar carteiras');
      setTerritories([]);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (territory = null) => {
    setEditing(territory);
    setFormData(territory ? {
      code: territory.code,
      name: territory.name,
      commercialHierarchyParent: territory.commercialHierarchyParent || '',
      commercialHierarchyChild: territory.commercialHierarchyChild || '',
      origin: territory.origin?.toString() || '1',
      minimumOrderValue: territory.minimumOrderValue?.toString() || '0',
      hasSuggestedLoad: Boolean(territory.hasSuggestedLoad),
    } : emptyForm);
    setDialogOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const payload = {
      ...formData,
      minimumOrderValue: Number(formData.minimumOrderValue || 0),
      origin: formData.origin ? Number(formData.origin) : undefined,
      commercialHierarchyParent: formData.commercialHierarchyParent || undefined,
      commercialHierarchyChild: formData.commercialHierarchyChild || undefined,
    };

    try {
      if (editing) {
        await api.updateTerritory(editing.id, payload);
        toast.success('Carteira atualizada');
      } else {
        await api.createTerritory(payload);
        toast.success('Carteira criada');
      }
      setDialogOpen(false);
      loadTerritories();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar carteira');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover esta carteira?')) return;
    try {
      await api.deleteTerritory(id);
      toast.success('Carteira removida');
      loadTerritories();
    } catch (error) {
      toast.error('Erro ao remover carteira');
    }
  };

  return (
    <div className="space-y-6" data-testid="territories-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Carteira</h1>
          <p className="text-muted-foreground mt-1">Cadastro baseado na tabela Territory</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Carteira
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar Carteira' : 'Nova Carteira'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Código</Label>
                    <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor mínimo</Label>
                    <Input type="number" min="0" step="0.01" value={formData.minimumOrderValue} onChange={(e) => setFormData({ ...formData, minimumOrderValue: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hierarquia pai</Label>
                    <Input value={formData.commercialHierarchyParent} onChange={(e) => setFormData({ ...formData, commercialHierarchyParent: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Hierarquia filha</Label>
                    <Input value={formData.commercialHierarchyChild} onChange={(e) => setFormData({ ...formData, commercialHierarchyChild: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Origem</Label>
                  <Input type="number" min="0" value={formData.origin} onChange={(e) => setFormData({ ...formData, origin: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={formData.hasSuggestedLoad} onCheckedChange={(checked) => setFormData({ ...formData, hasSuggestedLoad: Boolean(checked) })} />
                  Carga sugerida
                </label>
                <Button type="submit" className="w-full">{editing ? 'Atualizar' : 'Cadastrar'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por código, nome ou hierarquia..." searchFields={['Código', 'Nome', 'Hierarquia']} />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Hierarquia</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Origem</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Valor mínimo</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Carga</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {territories.map((territory) => (
                <tr key={territory.id} className="border-b hover:bg-muted/50">
                  <td className="px-6 py-4 text-sm font-mono">{territory.code}</td>
                  <td className="px-6 py-4 text-sm font-medium">{territory.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{territory.commercialHierarchyParent || '-'} / {territory.commercialHierarchyChild || '-'}</td>
                  <td className="px-6 py-4 text-sm text-center">{territory.origin ?? '-'}</td>
                  <td className="px-6 py-4 text-sm text-right">R$ {Number(territory.minimumOrderValue || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-center"><Badge variant={territory.hasSuggestedLoad ? 'default' : 'secondary'}>{territory.hasSuggestedLoad ? 'Sim' : 'Não'}</Badge></td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? <Switch checked={territory.enabled} onCheckedChange={() => api.toggleTerritory(territory.id).then(loadTerritories)} /> : <Badge>{territory.enabled ? 'Ativo' : 'Inativo'}</Badge>}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {isAdmin() && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openDialog(territory)}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(territory.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {!loading && territories.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MapPinned className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma carteira encontrada.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TerritoriesPage;
